import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { computeScores } from '../lib/scoring';
import { fmtDate, scoreColor, daysUntil } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Phone, Mail, MapPin, IdCard, Plus, FileText, CalendarClock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const ClientProfilePage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { getClient, getSessionsFor } = useApp();
  const client = getClient(id);
  const sessions = getSessionsFor(id);

  if (!client) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg font-semibold text-brand-indigo">Client not found.</div>
        <Button className="mt-4" onClick={() => nav('/clients')}>Back to clients</Button>
      </div>
    );
  }

  const enriched = sessions.map((s) => ({ ...s, scores: computeScores(s) }));
  const chartData = [...enriched].reverse().map((s) => ({
    date: fmtDate(s.session_date),
    overall: s.scores.overall
  }));
  const lastSession = enriched[0];
  const lastFollowUp = lastSession?.follow_up_date;
  const du = daysUntil(lastFollowUp);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1400px] mx-auto">
      <button onClick={() => nav('/clients')} className="text-xs text-brand-indigo font-semibold flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3" /> All clients</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-brand-indigo">{client.name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {client.phone}</div>
            <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {client.email}</div>
            <div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {client.city}</div>
            <div className="flex items-center gap-1"><IdCard className="h-3.5 w-3.5" /> PAN ••••{client.pan_last4}</div>
            <Badge variant="outline" className={client.kyc_status === 'verified' ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-amber-700 border-amber-300 bg-amber-50'}>
              KYC {client.kyc_status}
            </Badge>
          </div>
        </div>
        <Button onClick={() => nav(`/clients/${client.id}/mri/new`)} className="bg-brand-green hover:bg-brand-greenDark text-white">
          <Plus className="h-4 w-4 mr-2" /> Start New MRI
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-brand-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold text-brand-indigo">Score Trend</div>
                <div className="text-[11px] text-muted-foreground">Overall MRI score across visits</div>
              </div>
            </div>
            {chartData.length >= 2 ? (
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0DFF4" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#E0DFF4' }} />
                    <Line type="monotone" dataKey="overall" stroke="#1F135A" strokeWidth={3} dot={{ r: 5, fill: '#60B74F' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : chartData.length === 1 ? (
              <div className="text-center py-12">
                <div className="text-4xl font-bold text-brand-indigo">{chartData[0].overall}<span className="text-lg text-muted-foreground">/100</span></div>
                <div className="text-xs text-muted-foreground mt-2">Complete another session to see a trend</div>
              </div>
            ) : (
              <div className="text-center py-12 text-sm text-muted-foreground">No sessions yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-brand-border">
          <CardContent className="p-5">
            <div className="text-sm font-semibold text-brand-indigo mb-4">Last Session Summary</div>
            {lastSession ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-muted-foreground">Date</div>
                  <div className="text-sm font-medium text-brand-indigo">{fmtDate(lastSession.session_date)}</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-muted-foreground">Overall Score</div>
                  <div className={`text-lg font-bold ${scoreColor(lastSession.scores.overall).text}`}>{lastSession.scores.overall}</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-muted-foreground">Risk Profile</div>
                  <div className="text-sm font-medium text-brand-indigo">{lastSession.scores.riskLabel}</div>
                </div>
                {du !== null && (
                  <div className="mt-4 p-3 rounded-md bg-brand-bg border border-brand-border">
                    <div className="flex items-center gap-2 text-xs">
                      <CalendarClock className="h-4 w-4 text-brand-indigo" />
                      <div>
                        <div className="font-semibold text-brand-indigo">Next Follow-up</div>
                        <div className="text-muted-foreground">{fmtDate(lastFollowUp)} · {du === 0 ? 'today' : du > 0 ? `in ${du} days` : `${Math.abs(du)} days overdue`}</div>
                      </div>
                    </div>
                  </div>
                )}
                {lastSession.rm_notes && (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">RM Notes</div>
                    <div className="text-sm text-brand-indigo/80 leading-relaxed">{lastSession.rm_notes}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No sessions yet. Start the first MRI to build a baseline.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border">
            <div className="text-sm font-semibold text-brand-indigo">Session Timeline</div>
            <div className="text-[11px] text-muted-foreground">All Financial MRI sessions for this client</div>
          </div>
          <div className="divide-y divide-brand-border/60">
            {enriched.map((s) => {
              const c = scoreColor(s.scores.overall);
              return (
                <div key={s.id} className="px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-brand-bg">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold ${c.bg}`}>{s.scores.overall}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-brand-indigo text-sm">{fmtDate(s.session_date)} · {s.scores.riskLabel}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.rm_notes || s.next_action || 'No notes recorded'}</div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/clients/${client.id}/mri/${s.id}`} className="text-xs text-brand-indigo font-semibold hover:underline">Open</Link>
                    <Link to={`/clients/${client.id}/mri/${s.id}/report`} className="text-xs text-brand-green font-semibold hover:underline flex items-center gap-1"><FileText className="h-3 w-3" /> Report</Link>
                  </div>
                </div>
              );
            })}
            {enriched.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">No sessions recorded for this client yet.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientProfilePage;
